/**
 * GA4 integration — centralized tracking utility.
 *
 * Consent Mode v2 integration:
 * - consent defaults are set BEFORE gtag('config') via setConsentDefaults()
 * - GA4 script loads immediately but runs in cookieless/ping mode when denied
 * - On user consent update, gtag('consent','update') unlocks full tracking
 */

import { setConsentDefaults } from "@/lib/consent";

const GA4_MEASUREMENT_ID = "G-QX3NHWZWTM";

let initialized = false;

function ensureGtag(): boolean {
  if (!GA4_MEASUREMENT_ID) return false;
  if (typeof window === "undefined") return false;

  if (!initialized) {
    // 1. Set consent defaults BEFORE any gtag config (Consent Mode v2)
    setConsentDefaults();

    // 2. Inject gtag script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // 3. Initialize dataLayer & config
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(..._args: any[]) {
      (window as any).dataLayer.push(arguments);
    }
    (window as any).gtag = gtag;
    gtag("js", new Date());
    gtag("config", GA4_MEASUREMENT_ID, { send_page_view: true });

    initialized = true;
  }

  return typeof (window as any).gtag === "function";
}

// Auto-initialize on module load so gtag.js is present immediately
ensureGtag();

/**
 * Track a custom GA4 event.
 * Safe to call even if GA4 is not configured — will silently no-op.
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
) {
  if (!ensureGtag()) return;
  (window as any).gtag("event", eventName, params);
}

/**
 * Track a page view (called automatically by gtag config, but available for SPA navigation).
 */
export function trackPageView(path: string, title?: string) {
  if (!ensureGtag()) return;
  (window as any).gtag("event", "page_view", {
    page_path: path,
    page_title: title,
  });
}
